import { useQuery } from '@tanstack/react-query';

import { GET_SHARED_PATIENT_DIRECTORY_SETTING } from '../../contracts/keys';
import { getSharedPatientDirectorySetting } from '../api/settings';
import { useWorkspaceId } from './useWorkspaceId';

export { GET_SHARED_PATIENT_DIRECTORY_SETTING };

/**
 * Resolves `workspace-share_patients_enabled` for the actor's workspace.
 *
 * `data` is the strict boolean opt-in state: `true` only when the resolved
 * value is a literal `true`. An unresolved or malformed value reads as
 * not-enabled — the write path is the only place strictness matters, and the
 * section's enable flow surfaces a 409 there if this ever under-reports.
 *
 * Flag gating is the consumer's job. Pass `{ enabled }` to suppress the fetch
 * on flag-off or on-prem tenants that will short-circuit the compound gate
 * before the value is ever read (COLAB-385). The default preserves the
 * pre-existing unconditional-fetch behaviour for the settings section, which
 * mounts only when the flag is on.
 *
 * The key carries the active workspace id because the value belongs to one
 * workspace. Without it, a switch to workspace B reads A's cached boolean as
 * resolved (`isPending` false) until the reload lands, and the analytics cohort
 * pairs B with A's setting (COLAB-422). Invalidation stays prefix-based on
 * `[GET_SHARED_PATIENT_DIRECTORY_SETTING]`, which still matches every entry.
 */
export function useQuerySharedPatientDirectorySetting({
  enabled = true,
}: { enabled?: boolean } = {}) {
  const workspaceId = useWorkspaceId();
  return useQuery({
    queryKey: [GET_SHARED_PATIENT_DIRECTORY_SETTING, workspaceId],
    queryFn: () => getSharedPatientDirectorySetting(),
    select: (data) => data.value === true,
    staleTime: 60 * 1000,
    enabled,
  });
}
